import invariant from 'invariant';

import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { EmbedSearchWidgetLayoutHandleProps } from '#/modules/embedSearchWidget/layout/EmbedSearchWidgetLayoutHandleProps';
import type { EmbedSearchWidgetAlgoliaImportedVolopHit } from '#/modules/embedSearchWidget/volops/algolia/EmbedSearchWidgetAlgoliaImportedVolopHit';
import { embedSearchWidgetApiIsomorphicFetchVolop } from '#/modules/embedSearchWidget/volops/api/embedSearchWidgetApiFetchVolop';
import type { embedSearchWidgetViewVolopRoute } from '#/modules/embedSearchWidget/volops/routing/embedSearchWidgetVolops.routes';
import { getListingPublishedDetails } from '#/modules/listing/helpers/getListingPublishedDetails';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { getVolopPublishedDate } from '#/modules/listing/volops/helpers/getVolopPublishedDate';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaVolopsIndex } from '#/modules/search/algolia/searchAlgoliaVolopsIndex';
import { ClientOnlyLazySearchVolopsRefinements } from '#/modules/search/volops/components/Refinements/ClientOnlyLazySearchVolopsRefinements';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { algoliaGetObject } from '#/utils/algolia/algoliaGetObject';

import { EmbedSearchWidgetViewVolopPage } from './EmbedSearchWidgetViewVolopPage';
import { EmbedSearchWidgetViewImportedVolopPage } from './Imported/EmbedSearchWidgetViewImportedVolopPage';

export const handle = {
  searchRefinements: (
    <ClientOnlyLazySearchVolopsRefinements includeSourcesFacet />
  ),
  isSearchPage: false,
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { embedSearchWidgetId, volopId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewVolopRoute>) {
  if (volopId.startsWith('GOLDEN')) {
    const { appConfig } = await apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })();

    const importedVolopHit =
      await algoliaGetObject<EmbedSearchWidgetAlgoliaImportedVolopHit>({
        config: getSearchAlgoliaDefaultConfig({ appConfig }),
        index: searchAlgoliaVolopsIndex({ sort: 'relevance', appConfig }),
        id: volopId,
        attributes: ['*'],
        isUsingAlgoliaMock: appConfig.algolia.mock,
      }).catch(() => null);

    if (!importedVolopHit) {
      return idealistNotFound(null);
    }

    return { type: 'IMPORTED', importedVolopHit } as const;
  }

  try {
    const volop = await embedSearchWidgetApiIsomorphicFetchVolop({
      request,
      context,
    })(embedSearchWidgetId, volopId);

    if (volop.type === 'UNPUBLISHED_VOLOP') {
      return idealistNotFound(null);
    }

    return {
      type: 'VOLOP',
      volop,
      publishedDetails: getListingPublishedDetails({
        publishedDate: getVolopPublishedDate(volop),
        status: ListingViewStatus.PUBLISHED,
        context,
      }),
    } as const;
  } catch {
    return idealistNotFound(null);
  }
}

export default function EmbedSearchWidgetViewVolopHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'IMPORTED':
      return (
        <RouteModule title={loaderData.importedVolopHit.name}>
          <EmbedSearchWidgetViewImportedVolopPage
            importedVolopHit={loaderData.importedVolopHit}
          />
        </RouteModule>
      );
    case 'VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <EmbedSearchWidgetViewVolopPage
            volop={loaderData.volop}
            embedSearchWidget={embedSearchWidget}
            publishedDetails={loaderData.publishedDetails}
          />
        </RouteModule>
      );
  }
}
